import { useTheme } from '../theme'
import { ONGLET_PARENT } from '../routes'
import { usePanier } from '../contexts/panier'
import { useNavigationPortail, useActionsPortail } from '../contexts/navigation'
import { MVP } from '../mvp'

/**
 * Barre d'onglets basse — BASE PRESENTATIONNELLE partagee (S53) : le hub
 * (BarreHubClient / BarreHubProducteur ci-dessous) et les modules plein ecran
 * (Produits/Stands/Infos, Événements/Mon stand/Historique, Tournées...)
 * rendent la MEME barre, seuls les onglets changent.
 *
 * `onglets` : [{ k, e (emoji), l (libelle), badge? }] · `actif` : cle active
 * · `onSelect(k)`. Style : pilule flottante liquid glass (web2) / verre
 * sombre (web3) — pastille active vert identite, badge orange.
 */
export default function BarreOnglets({ onglets, actif, onSelect }) {
  const { isWeb3 } = useTheme()
  return (
    <nav className="fixed bottom-4 left-0 right-0 z-[40] px-3" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className={`max-w-md mx-auto flex p-1.5 rounded-3xl ${isWeb3 ? 'bg-[#0a0a0f]/85 border border-white/10 backdrop-blur-xl' : 'glass-surface'}`}>
        {onglets.map((t) => {
          const active = actif === t.k
          return (
            <button
              key={t.k}
              onClick={() => onSelect(t.k)}
              className={`relative flex-1 py-2.5 rounded-2xl flex flex-col items-center gap-0.5 text-[11px] font-bold transition-all ${active ? 'text-creme' : 'text-ink/60'}`}
              style={active ? { background: 'rgb(var(--c-green))' } : undefined}
            >
              <span className="text-[17px] leading-none">{t.e}</span>
              {t.l}
              {t.badge > 0 && (
                <span
                  className="absolute top-1 right-[18%] text-[9.5px] font-extrabold rounded-full px-1.5"
                  style={{ background: 'rgb(var(--c-primary))', color: 'rgb(var(--c-green))' }}
                >
                  {t.badge}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </nav>
  )
}

// --- Barres du hub : les onglets par casquette (prototype v7.2 : le 3e onglet
// producteur = « Offres » — l'Activite n'est pas un onglet, c'est un ecran
// ouvert depuis l'Accueil). L'onglet actif remonte au PARENT pour les
// sous-pages (table ONGLET_PARENT — Wallet allume « Compte », etc.).

function useBarreHub() {
  const { hubTab } = useNavigationPortail()
  const { setHubTab } = useActionsPortail()
  return { actif: ONGLET_PARENT[hubTab] || hubTab, onSelect: setHubTab }
}

export function BarreHubClient() {
  const { actif, onSelect } = useBarreHub()
  const { basket, productFavs } = usePanier()
  // MVP (S62) : Explorer et Favoris suivent leurs drapeaux
  const onglets = [
    { k: 'home', e: '🏠', l: 'Accueil' },
    ...(MVP.EXPLORER_CLIENT ? [{ k: 'explore', e: '🧭', l: 'Explorer' }] : []),
    ...(MVP.FAVORIS ? [{ k: 'favorites', e: '⭐', l: 'Favoris', badge: basket.count + productFavs.count }] : []),
    { k: 'account', e: '👤', l: 'Compte' },
  ]
  return <BarreOnglets onglets={onglets} actif={actif} onSelect={onSelect} />
}

export function BarreHubProducteur() {
  const { actif, onSelect } = useBarreHub()
  // MVP (S62) : un seul lieu ouvert → l'onglet « Évènements » est la page du
  // hub /producteur/marches (barre conservee), sans passer par l'Explorer.
  const onglets = [
    { k: 'home', e: '🏠', l: 'Accueil' },
    MVP.EXPLORER_PRODUCTEUR
      ? { k: 'explore', e: '🧭', l: 'Explorer' }
      : { k: 'marches', e: '🎪', l: 'Évènements' },
    { k: 'offers', e: '📦', l: 'Offres' },
    // S62 : « Ma page » (fiche vue par le client) prend la place du Compte,
    // qui reste accessible par la pastille du header.
    MVP.COMPTE_DANS_BARRE_PRODUCTEUR
      ? { k: 'account', e: '👤', l: 'Compte' }
      : { k: 'profilprod', e: '🌱', l: 'Ma page' },
  ]
  return <BarreOnglets onglets={onglets} actif={actif} onSelect={onSelect} />
}
